import { HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { of } from 'rxjs'
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { AppErrorHandlerService } from '../../services/app-error-handler/app-error-handler.service'
import { LogService } from '../../services/log.service'
import { UtilityService } from '../../services/utility.service'
import { AppState } from '../app.state'
import * as FeatureSPActions from './feature-sp.actions'
import { selectSPSearchOpen } from './feature-sp.selectors'
import { FeatureSPService } from './feature-sp.service'

@Injectable()
export class FeatureSPEffects {
   private _cn = this.constructor.name
   private _actions$ = inject(Actions)
   private _store = inject(Store<AppState>)
   private _logService = inject(LogService)
   private _service = inject(FeatureSPService)
   private _utilityService = inject(UtilityService)
   private _errorHandlerService = inject(AppErrorHandlerService)

   toggleSPSearch$ = createEffect(() =>
      this._actions$.pipe(
         ofType(FeatureSPActions.toggleSPSearch),
         withLatestFrom(this._store.pipe(select(selectSPSearchOpen))),
         switchMap(([action, isSearchOpen]) => {
            return of(isSearchOpen ? FeatureSPActions.closeSPSearch() : FeatureSPActions.openSPSearch())
         })
      )
   )

   toggleAdvancedSPSearch$ = createEffect(() =>
      this._actions$.pipe(
         ofType(FeatureSPActions.toggleAdvancedSPSearch),
         withLatestFrom(this._store.pipe(select((state) => state.featureSearchProjects.isAdvancedSearch))),
         switchMap(([action, isAdvancedSearch]) => {
            return of(isAdvancedSearch ? FeatureSPActions.setFalseIsAdvancedSPSearch() : FeatureSPActions.setIsAdvancedSPSearch({ isAdvanced: true }))
         })
      )
   )

   actionSearchProject$ = createEffect(() =>
      this._actions$.pipe(
         ofType(FeatureSPActions.search),
         switchMap((action) =>
            this._service.search(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'loadStatutes effect', 'response:', res)),
               map((result) => FeatureSPActions.searchSuccess({ result })),
               catchError((error: HttpErrorResponse) => {
                  return of(FeatureSPActions.searchFailure({ error: error }))
               })
            )
         )
      )
   )

   exportExcelResults$ = createEffect(() =>
      this._actions$.pipe(
         ofType(FeatureSPActions.exportExcelResults),
         switchMap((action) =>
            this._service.exportExcelResults(action.searchFields).pipe(
               tap((res) => this._logService.debug(this._cn, 'exportExcelResults effect', 'response:', res)),
               map((response) => {
                  if (response.body) {
                     this._utilityService.downloadFile(response)
                     return FeatureSPActions.exportExcelResultsSuccess()
                  } else {
                     return FeatureSPActions.exportExcelResultsFailure({ error: new HttpErrorResponse({ error: '0 - No blob in response to download' }) })
                  }
               }),

               catchError((error: HttpErrorResponse) => {
                  return of(FeatureSPActions.exportExcelResultsFailure({ error: error }))
               })
            )
         )
      )
   )

   private _actionsForErrorNotificationMessage = [FeatureSPActions.searchFailure, FeatureSPActions.exportExcelResultsFailure]

   listenAllFailureAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForErrorNotificationMessage),
            map((errorResponse) => this._errorHandlerService.handleError(errorResponse.error))
         ),
      { dispatch: false }
   )
}
